/**
 * Sign-in for the app and the console: completes the redirect back from Keycloak, restores the session after a
 * reload (`prompt=none`), and starts the login when nobody is signed in. Paths that fail the #6 rule are not
 * signed in for: the router shows "We can't find this page" without a round trip to Keycloak.
 */
import { createContext, useContext, useEffect, useRef, useState, useSyncExternalStore, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { KeyRound } from "lucide-react";
import { Button } from "@brevalt/ui";
import { isSafeAppPath } from "../routing";
import { NotFoundState, ServerErrorState } from "../errors/system-states";
import type { AuthSession, AuthStatus, SessionUser } from "./session";

const AuthContext = createContext<AuthSession | null>(null);

export function AuthProvider({ session, children }: { session: AuthSession; children: ReactNode }) {
  return <AuthContext.Provider value={session}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthSession {
  const session = useContext(AuthContext);
  if (!session) throw new Error("useAuth outside AuthProvider");
  return session;
}

export function useAuthStatus(): AuthStatus {
  const session = useAuth();
  return useSyncExternalStore(session.subscribe, () => session.status);
}

export function useSessionUser(): SessionUser | null {
  const session = useAuth();
  const status = useAuthStatus();
  return status === "signed-in" ? session.sessionUser : null;
}

export type SignedOutReason = "user" | "idle" | "everywhere";

/** `/?signedOut=idle` after an idle or explicit sign-out: show the reason, sign in only on request. */
export function signedOutReason(search: string): SignedOutReason | null {
  const value = new URLSearchParams(search).get("signedOut");
  return value === "user" || value === "idle" || value === "everywhere" ? value : null;
}

function SignedOut({
  reason,
  idleMinutes,
  unconfirmed,
  onSignIn,
}: {
  reason: SignedOutReason;
  idleMinutes: number;
  /** Keycloak's sign-out could not be confirmed (unreachable, or the navigation never left the page). */
  unconfirmed: boolean;
  onSignIn: () => void;
}) {
  const { t } = useTranslation();
  const h1 = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    h1.current?.focus();
  }, []);
  return (
    <main id="main" className="mx-auto flex max-w-lg flex-col items-center px-4 py-16 text-center">
      <h1 ref={h1} tabIndex={-1} className="heading text-xl outline-none">
        {t("misc.your_session_ended")}
      </h1>
      <p className="mt-2 text-sm text-text-muted">
        {reason === "idle"
          ? t("shell.signed_out_after_inactivity", { minutes: idleMinutes })
          : t("shell.you_signed_out")}
      </p>
      {unconfirmed && <p className="mt-2 text-sm text-text-muted">{t("shell.sign_out_unconfirmed")}</p>}
      <p className="mt-2 text-xs text-text-muted">{t("misc.deadline_alerts_keep_running_while_you")}</p>
      <div className="mt-6">
        <Button variant="primary" icon={KeyRound} onClick={onSignIn}>
          {t("misc.sign_in_again")}
        </Button>
      </div>
    </main>
  );
}

function Busy() {
  const { t } = useTranslation();
  return (
    <p role="status" className="p-8 text-center text-sm text-text-muted">
      {t("shell.signing_you_in")}
    </p>
  );
}

/**
 * Over the signed-in tree while it is not usable (a redirect under way, a re-login, Keycloak unreachable): a modal
 * in a portal above the toasts. It takes focus (screen readers announce it), and every other top-level node of
 * the page (the app, Radix dialogs and toasts in their portals) is inert until it closes. Opaque once a sign-out
 * started: nothing of the signed-in page shows any more.
 */
function Overlay({ status, opaque }: { status: AuthStatus; opaque: boolean }) {
  const { t } = useTranslation();
  const node = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const own = node.current;
    if (!own) return;
    // Inert first: an open Radix dialog's focus trap can then no longer pull focus back into its content.
    // Only nodes this overlay made inert are given back (one that was inert before stays so).
    const made = new Set<Element>();
    const makeInert = (el: Element) => {
      if (el.contains(own) || el.hasAttribute("inert")) return;
      el.setAttribute("inert", "");
      made.add(el);
    };
    for (const el of Array.from(document.body.children)) makeInert(el);
    // Portals that open later (a toast, a dialog that was still opening) become inert too.
    const observer = new MutationObserver((records) => {
      for (const r of records) for (const n of Array.from(r.addedNodes)) if (n instanceof Element) makeInert(n);
    });
    observer.observe(document.body, { childList: true });
    own.focus();
    return () => {
      observer.disconnect();
      for (const el of made) el.removeAttribute("inert");
    };
  }, []);
  const text = status === "unavailable" ? t("shell.session_unavailable") : t("misc.one_moment");
  return createPortal(
    <div
      ref={node}
      role="alertdialog"
      aria-modal="true"
      aria-label={text}
      tabIndex={-1}
      data-auth-overlay={status}
      // Above the toasts (the highest layer of the kit); the CSSOM sets it, which the CSP allows.
      style={{ zIndex: "calc(var(--z-toast) + 1)" }}
      // pointer-events-auto: an open Radix modal sets pointer-events none on <body>; the overlay must still catch
      // every click.
      className={`pointer-events-auto fixed inset-0 grid place-items-center outline-none ${opaque ? "bg-bg" : "bg-scrim"}`}
    >
      <p role="status" className="rounded-card bg-surface px-5 py-4 text-sm shadow-overlay">
        {text}
      </p>
    </div>,
    document.body,
  );
}

/**
 * Renders `children` once signed in. `location` defaults to the browser's; tests pass one.
 * `replace` updates the address after the callback (the router reads it when it mounts).
 * A path that fails the #6 rule gets `notFound` alone: no sign-in, and none of the signed-in tree.
 */
export function AuthGate({
  children,
  notFound = <NotFoundState homeHref="/" />,
  idleMinutes = 30,
  location = typeof window !== "undefined" ? window.location : undefined,
  replace = (path: string) => {
    window.history.replaceState(null, "", path);
  },
}: {
  children: ReactNode;
  notFound?: ReactNode;
  idleMinutes?: number;
  location?: Pick<Location, "pathname" | "search" | "hash" | "href">;
  replace?: (path: string) => void;
}) {
  const session = useAuth();
  const status = useAuthStatus();
  const reason = location ? signedOutReason(location.search) : null;
  const unconfirmed = location ? new URLSearchParams(location.search).get("unconfirmed") === "1" : false;
  const [phase, setPhase] = useState<"callback" | "ready" | "signed-out" | "error">(() =>
    location && session.isCallback(location.pathname) ? "callback" : reason ? "signed-out" : "ready",
  );
  const path = location ? location.pathname + location.search + location.hash : "/";
  const unsafe = !isSafeAppPath(path);

  // The redirect back from Keycloak.
  const handled = useRef(false);
  useEffect(() => {
    if (phase !== "callback" || handled.current || !location) return;
    handled.current = true;
    session
      .completeCallback(location.href)
      .then(async ({ returnTo, interactive, kind, signingOut }) => {
        // A sign-out is under way: its navigation to the signed-out page follows; nothing signs in.
        if (signingOut) return;
        if (interactive) {
          await session.signIn(returnTo, kind === "login-mfa" ? { kind } : {});
          return;
        }
        replace(returnTo);
        setPhase("ready");
      })
      .catch(() => {
        setPhase("error");
      });
  }, [phase, session, location, replace]);

  // Nobody signed in (first visit or a reload: tokens are in memory only): Keycloak's SSO session signs in again
  // without a form (prompt=none); without one, Keycloak answers login_required and the login form follows.
  const needsLogin = phase === "ready" && (status === "signed-out" || status === "unknown") && !unsafe;
  // "started": the silent sign-in is under way; "given-back": it left and the page came back without a login.
  const redirected = useRef<"no" | "started" | "given-back">("no");
  // Back/forward cache: a page restored after it left for Keycloak may sign in again.
  const [, setRestored] = useState(0);
  useEffect(() => {
    const onPageShow = (e: PageTransitionEvent) => {
      if (!e.persisted) return;
      redirected.current = "no";
      session.resetLeaving();
      setRestored((n) => n + 1);
    };
    window.addEventListener("pageshow", onPageShow);
    return () => {
      window.removeEventListener("pageshow", onPageShow);
    };
  }, [session]);
  useEffect(() => {
    if (!needsLogin) {
      // The tab left for Keycloak (status "leaving"): if it ever needs a login again, the page was given back.
      if (redirected.current === "started") redirected.current = "given-back";
      return;
    }
    if (redirected.current === "given-back") {
      // The silent sign-in gave the page back without leaving it (its navigation never committed): an error
      // with a way to try again, not an endless "Signing you in".
      setPhase("error");
      return;
    }
    // StrictMode runs this effect twice with the same values: the redirect of the first run is under way.
    if (redirected.current === "started") return;
    redirected.current = "started";
    // The site's "Start free trial" (`/?signup=1`): straight to Keycloak's registration (prompt=create); a silent
    // sign-in would never show it. The flag is not kept in the return path.
    const params = new URLSearchParams(location?.search ?? "");
    const signup = params.get("signup") === "1";
    params.delete("signup");
    const rest = params.toString();
    const target = signup ? `${location?.pathname ?? "/"}${rest ? `?${rest}` : ""}` : path;
    session.signIn(target, signup ? { create: true } : { silent: true }).catch(() => {
      setPhase("error");
    });
  }, [needsLogin, session, path, location]);

  // Once the signed-in tree has mounted it stays mounted (state, step-up, idle timer, query cache): while the
  // tab leaves for Keycloak, signs in again or cannot reach Keycloak, the tree is inert under an overlay.
  const [mounted, setMounted] = useState(false);
  if (!mounted && phase === "ready" && status === "signed-in") setMounted(true);
  // The person of the kept tree: the last signed-in sub (it stays while leaving or re-signing in).
  const [sub, setSub] = useState<string | null>(null);
  const liveSub = useSyncExternalStore(session.subscribe, () => session.sessionUser?.sub ?? null);
  const currentSub = status === "signed-in" ? liveSub : sub;
  if (currentSub !== sub) setSub(currentSub);

  // A deliberate new sign-in (signed-out page, error page): Keycloak asks for the credentials (prompt=login),
  // never quietly re-enters a session the user just left.
  const signInAgain = () => {
    session.signIn("/", { fresh: true }).catch(() => {
      setPhase("error");
    });
  };
  if (unsafe && phase !== "callback") return <>{notFound}</>;
  if (phase === "error") return <ServerErrorState onRetry={signInAgain} />;
  if (phase === "signed-out" && reason) {
    return <SignedOut reason={reason} idleMinutes={idleMinutes} unconfirmed={unconfirmed} onSignIn={signInAgain} />;
  }
  if (mounted) {
    const usable = status === "signed-in";
    return (
      <>
        {/* One tree per person: another sub (a re-login as someone else) starts with fresh state. */}
        <div key={sub ?? "nobody"} inert={!usable} aria-busy={!usable || undefined} data-auth-status={status}>
          {children}
        </div>
        {!usable && <Overlay status={status} opaque={session.isSigningOut} />}
      </>
    );
  }
  return <Busy />;
}
